import { cpf } from 'cpf-cnpj-validator'
import * as Yup from 'yup'

import { VALID_DDDS } from 'utils/masks/validDdds'
import { isValidPersonFullName } from 'utils/validators/personName'

const MIN_LEGAL_REPRESENTATIVE_AGE = 18

const commonTextValidation = (maxLength: number) =>
  Yup.string()
    .min(2, 'Deve conter no mínimo 2 caracteres')
    .max(maxLength, `Deve conter no máximo ${maxLength} caracteres`)
    .required('Este campo é obrigatório')

const personNameValidation = (maxLength: number) =>
  commonTextValidation(maxLength).test('valid-person-name', 'Informe um nome válido', value =>
    Boolean(value && isValidPersonFullName(value))
  )

const parseBirthDate = (value: string): Date | null => {
  const match = value?.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  if (!match) return null

  const [, day, month, year] = match.map(Number)
  const date = new Date(year, month - 1, day)
  const isRealCalendarDate = date.getDate() === day && date.getMonth() === month - 1 && date.getFullYear() === year

  return isRealCalendarDate ? date : null
}

const isAtLeast18YearsOld = (birthDate: Date): boolean => {
  const today = new Date()
  const ageCutoff = new Date(today.getFullYear() - MIN_LEGAL_REPRESENTATIVE_AGE, today.getMonth(), today.getDate())

  return birthDate <= ageCutoff
}

const legalRepresentativeSchema = Yup.object().shape({
  nome: personNameValidation(100),
  cpf: Yup.string()
    .required('Este campo é obrigatório')
    .test('valid-cpf', 'Preencha um CPF válido', value => !!value && cpf.isValid(value)),
  dataNascimento: Yup.string()
    .required('Este campo é obrigatório')
    .test('valid-date', 'Preencha uma data válida no formato DD/MM/AAAA', value => !!value && !!parseBirthDate(value))
    .test('valid-age', `O representante legal deve ter no mínimo ${MIN_LEGAL_REPRESENTATIVE_AGE} anos`, value => {
      const birthDate = value && parseBirthDate(value)
      return !!birthDate && isAtLeast18YearsOld(birthDate)
    }),
  email: Yup.string().email('Preencha um e-mail válido').required('Este campo é obrigatório')
})

const optionalLegalRepresentativeSchema = Yup.object().shape({
  nome: Yup.string(),
  cpf: Yup.string(),
  dataNascimento: Yup.string(),
  email: Yup.string()
})

const stepOneValidationSchema = Yup.object().shape({
  nomeCompleto: personNameValidation(200),
  telefone: Yup.string()
    .required('Este campo é obrigatório')
    .test('correct-number', 'O número deve estar no formato (XX) 9XXXX-XXXX', phone => {
      if (!phone || phone.length < 15) return false

      const formatedPhone = phone.replaceAll(/[()-]/g, '')
      return /^\d{2}\s9\d{4}\d{4}$/.test(formatedPhone)
    })
    .test('valid-ddd', 'Preencha um DDD válido', phone => {
      if (!phone) return false
      const ddd = phone.replace(/\D/g, '').slice(0, 2)
      return VALID_DDDS.includes(ddd)
    }),
  cep: Yup.string()
    .required('Este campo é obrigatório')
    .matches(/^\d{5}-?\d{3}$/, 'Preencha um CEP válido, com 8 dígitos'),
  razaoSocial: commonTextValidation(200),
  nomeFantasia: commonTextValidation(100),
  cnae: Yup.string()
    .required('Este campo é obrigatório')
    .matches(/^\d{4}-\d\/\d{2}$/, 'O CNAE deve estar no formato 0000-0/00')
})

const stepTwoValidationSchema = Yup.object().shape({
  assinaturaConjunta: Yup.string().oneOf(['SIM', 'NAO']).required('Este campo é obrigatório'),
  representanteLegal1: legalRepresentativeSchema,
  representanteLegal2: Yup.object().when('assinaturaConjunta', {
    is: 'SIM',
    then: () => legalRepresentativeSchema,
    otherwise: () => optionalLegalRepresentativeSchema
  })
})

export const validationSchema = stepOneValidationSchema.concat(stepTwoValidationSchema)
